// Which clicks the widget opens: legacy DOCBOX_LINK_MODE "auto", the default, which every site
// runs (findPdfUrlFromEvent, public/docbox.js:35847). Any other click is left to the browser.
import {
  deferDriveFoldersToHost,
  ignoreOptions,
  skipGoogleSlides,
  type IgnoreOptions,
} from "./site-options";

/** A URL that ends in .pdf, before any query or hash (looksLikePdfUrl, docbox.js:35216). */
export function looksLikePdfUrl(url: string): boolean {
  return /\.pdf(?:$|[?#])/i.test(url.trim());
}

/** Legacy ".pdf" links that now redirect to HTML pages (isKnownNonPdfUrl, docbox.js:35344). */
function isKnownNonPdfUrl(url: string): boolean {
  const s = url.trim();
  return (
    /^https:\/\/opensource\.adobe\.com\/dc-acrobat-sdk-docs\/?([?#].*)?$/i.test(
      s,
    ) ||
    /^https:\/\/www\.adobe\.com\/content\/dam\/acom\/en\/devnet\/acrobat\/pdfs\/pdf_open_parameters\.pdf([?#].*)?$/i.test(
      s,
    ) ||
    /^https:\/\/chsaanow\.com\/documents\//i.test(s)
  );
}

/**
 * A Google Calendar page: an event, an "add to calendar" link, an embed or a feed. Never a
 * document, even when its text names a PDF or its query carries a file name. Not in legacy.
 */
function isCalendarUrl(s: string): boolean {
  return (
    /^https?:\/\/calendar\.google\.com(?:[/?#]|$)/i.test(s) ||
    /^https?:\/\/(?:www\.)?google\.com\/calendar(?:[/?#]|$)/i.test(s)
  );
}

/** Google sign-in redirects and thumbnail tiles (isBlockedIntermediaryDocumentUrl, :35227). */
function isBlockedIntermediaryDocumentUrl(s: string): boolean {
  return (
    /^https:\/\/accounts\.google\.com\/RotateCookiesPage(?:[/?#]|$)/i.test(s) ||
    /^https:\/\/accounts\.google\.com\/(?:ServiceLogin|signin|v3\/signin)(?:[/?#]|$)/i.test(
      s,
    ) ||
    /^https:\/\/play\.google\.com\/log(?:[/?#]|$)/i.test(s) ||
    /^https:\/\/drive\.google\.com\/(?:u\/\d+\/)?drive-viewer\//i.test(s) ||
    /^https:\/\/drive\.google\.com\/viewer\/img\?/i.test(s) ||
    /^https:\/\/docs\.google\.com\/viewerng\/(?:img|presspage)\?/i.test(s) ||
    /^https:\/\/docs\.google\.com\/viewer\/img\?/i.test(s) ||
    /^blob:https:\/\/docs\.google\.com\//i.test(s)
  );
}

/** "Make a copy" links keep Google's own prompt (isGoogleMakeACopyUrl, :35598). */
function isGoogleMakeACopyUrl(s: string): boolean {
  return (
    /^https?:\/\/docs\.google\.com\//i.test(s) &&
    /\/(?:document|spreadsheets|presentation|drawings|forms)\/(?:u\/\d+\/)?d\/(?:e\/)?[a-zA-Z0-9_-]+\/copy(?:[/?#]|$)/i.test(
      s,
    )
  );
}

/**
 * Video, audio and WebP, by URL or file name. Never a document (docboxIsNonDocumentMediaHint,
 * :35360). JPEG and PNG stay documents: calendars are often images.
 */
export function isNonDocumentMedia(url: string, fileName = ""): boolean {
  const s = url.trim();
  const blob = `${fileName.trim()} ${s}`.toLowerCase();
  if (!blob.trim()) return false;
  return (
    /\.(mp4|m4v|mov|webm|mkv|avi|mpeg|mpg|wmv|flv|3gp|ogv)(?:$|[?#&\s])/i.test(
      blob,
    ) ||
    /\.(mp3|m4a|wav|aac|ogg|flac|wma|aiff?)(?:$|[?#&\s])/i.test(blob) ||
    /\.webp(?:$|[?#&\s])/i.test(blob) ||
    /[?&]mimeType=(?:video|audio)(?:%2F|\/)/i.test(s) ||
    /[?&]mimeType=image(?:%2F|\/)webp/i.test(s) ||
    /video\/(?:mp4|webm|quicktime|x-msvideo|x-matroska)/i.test(blob) ||
    /audio\/(?:mpeg|mp4|wav|ogg|x-wav|aac)/i.test(blob) ||
    /image\/webp/i.test(blob) ||
    /drive\.google\.com\/(?:u\/\d+\/)?drive-viewer\//i.test(s)
  );
}

/** Published slideshows: /d/e/… and /pub, /pubembed, /embed (:7421). */
function isGoogleSlidesPublishedOrEmbedUrl(s: string): boolean {
  return (
    /docs\.google\.com\/presentation\/(?:u\/\d+\/)?d\/e\//i.test(s) ||
    /docs\.google\.com\/presentation\/[^?#]+\/(?:pubembed|pub|embed)(?:[?#]|$)/i.test(
      s,
    )
  );
}

/**
 * Slides the host keeps (docboxDeferGoogleSlidesToHost, :7436): published slideshows always,
 * every Slides link with DOCBOX_SKIP_GOOGLE_SLIDES. An edit or view link opens in the widget.
 */
function deferGoogleSlidesToHost(s: string): boolean {
  if (skipGoogleSlides() && /docs\.google\.com\/presentation\//i.test(s)) {
    return true;
  }
  return isGoogleSlidesPublishedOrEmbedUrl(s);
}

/** The folder id of a Drive folder link, or "" (extractGoogleDriveFolderId, :7327). */
export function driveFolderId(url: string): string {
  const s = url.trim();
  const m =
    s.match(
      /drive\.google\.com\/drive\/(?:u\/\d+\/)?folders\/([a-zA-Z0-9_-]+)/i,
    ) ??
    s.match(
      /drive\.google\.com\/embeddedfolderview\?[^#]*\bid=([a-zA-Z0-9_-]+)/i,
    );
  return m ? m[1] : "";
}

/** A Drive file id in an /open, /uc or viewer link (:34702, :34716). */
function driveIdFromOpenOrViewerUrl(s: string): boolean {
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return false;
  }
  if (!/\.drive\.google\.com$/i.test(u.hostname)) return false;
  const id = u.searchParams.get("id");
  if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) return false;
  const path = u.pathname.replace(/\/$/, "") || "/";
  if (path === "/open" || path === "/uc") return true;
  const lower = u.pathname.toLowerCase();
  return lower === "/viewer/img" || lower.startsWith("/viewerng/");
}

/** docs.google.com/drivesharing/clientmodel?id=… (:34690). */
function isClientmodelUrl(s: string): boolean {
  if (!/^https?:\/\/docs\.google\.com\/drivesharing\/clientmodel\?/i.test(s))
    return false;
  try {
    return /^[a-zA-Z0-9_-]+$/.test(new URL(s).searchParams.get("id") ?? "");
  } catch {
    return false;
  }
}

/**
 * A direct PDF, or a Google, SharePoint, OneDrive, Dropbox, Office Online, Notion or Office file
 * link the widget can open (looksLikeDocumentUrl, docbox.js:35607).
 */
export function looksLikeDocumentUrl(url: string): boolean {
  const s = url.trim();
  // The URL without its query and hash. Legacy tests the provider patterns against the whole
  // URL, so a link whose query carries a Docs or Dropbox URL opens. The widget tests them here.
  const base = s.replace(/[?#].*$/, "");
  if (isCalendarUrl(s)) return false;
  if (isBlockedIntermediaryDocumentUrl(s)) return false;
  if (isGoogleMakeACopyUrl(s)) return false;
  if (isNonDocumentMedia(s)) return false;
  if (deferGoogleSlidesToHost(s)) return false;
  // Google's viewer wrapping a SharePoint or PDF download (SchoolBlocks Office 365 embeds).
  if (
    /^https?:\/\/docs\.google\.com\/(?:viewerng\/viewer|viewer)(?:\?|#|$)/i.test(
      s,
    ) &&
    /[?&]url=/i.test(s)
  ) {
    try {
      const inner = new URL(s).searchParams.get("url");
      if (
        inner &&
        (looksLikePdfUrl(inner) ||
          /sharepoint\.com/i.test(inner) ||
          looksLikeDocumentUrl(inner))
      ) {
        return true;
      }
    } catch {
      // Not a URL. The checks below decide.
    }
  }
  // The whole URL, as legacy tests it: a download script such as view?file=menu.pdf opens.
  if (looksLikePdfUrl(s)) return true;
  if (/drive\.google\.com\/file\/(?:u\/\d+\/)?d\//.test(base)) return true;
  if (driveIdFromOpenOrViewerUrl(s)) return true;
  if (
    /^https?:\/\/drive\.usercontent\.google\.com\/download\?.*[?&]id=/.test(s)
  )
    return true;
  if (/docs\.google\.com\/file\/(?:u\/\d+\/)?d\//.test(base)) return true;
  if (
    /docs\.google\.com\/(?:document|spreadsheets|presentation|drawings)\/d\//.test(
      base,
    )
  ) {
    return true;
  }
  if (/drive\.google\.com\/drive\/(?:u\/\d+\/)?folders\//.test(base))
    return true;
  if (/^https?:\/\/drive\.google\.com\/embeddedfolderview\?/i.test(s))
    return true;
  if (isClientmodelUrl(s)) return true;

  // SharePoint and OneDrive.
  if (/\.sharepoint\.com\/:(?:[bfwx]):\//i.test(base)) return true;
  if (
    /\/_layouts\/15\/(?:download|Doc|WopiFrame|guestaccess)\.aspx/i.test(base)
  ) {
    return true;
  }
  if (/1drv\.ms\/u\//i.test(base)) return true;
  if (
    /onedrive\.live\.com\//i.test(base) &&
    (/(?:download|redir|embed)\b/i.test(s) ||
      /[?&](?:download|cid|resid|authkey)=/i.test(s))
  ) {
    return true;
  }

  // Dropbox.
  if (/dropbox\.com\/(?:s\/|scl\/fi\/)/i.test(base)) return true;
  if (/dl\.dropboxusercontent\.com\//i.test(base)) return true;

  // Office Online, and SharePoint or OneDrive for Business viewers.
  if (/view\.officeapps\.live\.com\/op\/(?:embed|view)\.aspx/i.test(base))
    return true;
  if (
    /\.sharepoint\.com\/.*\/_layouts\/15\/onedrive\.aspx/i.test(base) &&
    /[?&]id=/i.test(s)
  ) {
    return true;
  }
  if (/\.sharepoint\.com\/.*\/_layouts\/15\/embed\.aspx/i.test(base))
    return true;

  // Files hosted by Notion.
  if (/(?:www\.)?(?:notion\.so|notion\.site)\/image\//i.test(base)) return true;
  if (/^https?:\/\/file\.notion\.so\//i.test(s)) return true;
  if (/prod-files-secure\.s3[.-]us-west-2\.amazonaws\.com\//i.test(base))
    return true;
  if (
    /s3[.-]us-west-2\.amazonaws\.com\/secure\.notion-static\.com\//i.test(base)
  ) {
    return true;
  }

  // Office, ODF, RTF, text and CSV files.
  return /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf|txt|csv)(?:[?#]|$)/i.test(s);
}

/** Link text or label that names a PDF: "Menu.pdf", "Menu PDF", "Menu (PDF)", "PDF" (:35895). */
function namesPdf(text: string): boolean {
  return (
    /\.pdf\s*$/i.test(text) ||
    /\s+PDF\s*$/i.test(text) ||
    /\s+\(PDF\)\s*$/i.test(text) ||
    /^PDF$/i.test(text)
  );
}

/** The URL a link-like element opens, if it is a document link (:35884-35915). */
function linkUrl(el: Element, raw: string | null): string | null {
  if (!raw) return null;
  const url = absoluteUrl(raw, el);
  if (isKnownNonPdfUrl(url) || isCalendarUrl(url)) return null;
  if (looksLikeDocumentUrl(url)) return url;
  const text = (el.textContent ?? "").trim();
  const label = el.getAttribute("aria-label") || el.getAttribute("title") || "";
  return namesPdf(text) || namesPdf(label) ? url : null;
}

function absoluteUrl(raw: string, el: Element): string {
  try {
    return new URL(raw, el.ownerDocument.baseURI).href;
  } catch {
    return raw;
  }
}

/**
 * The document URL a click opens, and the element that carries it, from the event path
 * (findPdfUrlFromEvent, :35847). Per element, nearest first: data-docbox-url or data-pdf-url (any
 * URL), then an <a> or <area> href, then a role="link" element's href, data-href or data-url.
 */
export function documentTargetFromPath(
  path: readonly EventTarget[],
): { url: string; element: Element } | null {
  for (const node of path) {
    if (!isElement(node)) continue;
    const dataUrl =
      node.getAttribute("data-docbox-url") || node.getAttribute("data-pdf-url");
    if (dataUrl) return { url: absoluteUrl(dataUrl, node), element: node };
    const tag = node.tagName.toUpperCase();
    if (tag === "A" || tag === "AREA") {
      const url = linkUrl(node, node.getAttribute("href"));
      if (url) return { url, element: node };
    }
    if ((node.getAttribute("role") ?? "").toLowerCase() === "link") {
      const raw =
        node.getAttribute("href") ||
        node.getAttribute("data-href") ||
        node.getAttribute("data-url");
      const url = linkUrl(node, raw);
      if (url) return { url, element: node };
    }
  }
  return null;
}

/**
 * The file name a link names, for the media check (docboxInferOpenFileNameHint, :35512): its
 * download, aria-label or title attribute, its text when that ends in an extension, or the last
 * path segment of the URL.
 */
function fileNameHint(url: string, el: Element): string {
  const attr =
    el.getAttribute("download") ||
    el.getAttribute("aria-label") ||
    el.getAttribute("title") ||
    "";
  if (attr.trim()) return attr.trim();
  const text = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  if (text && /\.\w{2,5}$/i.test(text)) return text;
  const last = url.split("?")[0].split("#")[0].split("/").pop() ?? "";
  if (
    last &&
    /\.\w{2,5}$/i.test(last) &&
    !/^(preview|view|edit)$/i.test(last)
  ) {
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  }
  return "";
}

/** A video player embed of the host page (docboxHostElementLooksLikeVideoEmbed, :35417). */
function inVideoEmbed(el: Element): boolean {
  const cls = el.getAttribute("class") ?? "";
  if (
    /VideoBlock|jwplayer|vimeo|youtube|wistia|brightcove|videojs/i.test(cls)
  ) {
    return true;
  }
  const src = el.getAttribute("src") || el.getAttribute("data-src") || "";
  if (
    /youtube\.com\/embed|youtu\.be\/|player\.vimeo\.com|wistia\.(?:com|net)|brightcove\.net|videojs/i.test(
      src,
    )
  ) {
    return true;
  }
  return !!el.closest(
    "[class*='VideoBlock'], [class*='videoContainer'], [class*='videoTransitionContainer'], [class*='embedResponsive16by9']",
  );
}

/** A Drive WebP title card near the link: the file is a video (:35380). */
function nearDriveWebpTitleCard(el: Element): boolean {
  const near = el.closest("article, section, li, td, div");
  if (!near) return false;
  return [...near.querySelectorAll("img[src]")].some((img) => {
    const src = img.getAttribute("src") ?? "";
    return (
      /drive\.google\.com\/(?:u\/\d+\/)?drive-viewer\//i.test(src) ||
      /\.webp(?:$|[?#])/i.test(src)
    );
  });
}

/** Media the widget leaves to the host (docboxShouldDeferMediaToHost, :35478). */
function deferMediaToHost(url: string, el: Element): boolean {
  if (inVideoEmbed(el)) return true;
  if (isNonDocumentMedia(url, fileNameHint(url, el))) return true;
  return (
    /(?:drive|docs)\.google\.com\/file\/(?:u\/\d+\/)?d\//i.test(url) &&
    nearDriveWebpTitleCard(el)
  );
}

const XHTML = "http://www.w3.org/1999/xhtml";

/**
 * An element of the page or of a same-origin iframe. instanceof Element is false for a node of an
 * iframe, because each window has its own Element.
 */
function isElement(node: EventTarget | null): node is Element {
  return (node as Node | null)?.nodeType === 1;
}

export type DocumentLink = {
  element: HTMLElement;
  url: string;
  title: string;
};

/**
 * The document link an event happened on, if any. Links inside the widget's own UI are skipped:
 * an event from inside its shadow root reaches the document retargeted to the host element. The
 * Lambda refuses any URL that is not HTTPS, so such a link opens as a plain link.
 */
export function documentLinkFor(event: Event): DocumentLink | null {
  const target = event.target;
  if (!isElement(target)) return null;
  if (target.closest("[data-docmersion-widget]")) return null;
  const path = event.composedPath();
  if (ignoredPath(path, ignoreOptions())) return null;
  const found = documentTargetFromPath(path);
  if (!found || found.element.namespaceURI !== XHTML) return null;
  const { url } = found;
  const element = found.element as HTMLElement;
  if (!url.startsWith("https://") || hasTemporaryAuth(url)) return null;
  // The click-time checks of the legacy click handler (docbox.js:35976-35980).
  if (driveFolderId(url) && deferDriveFoldersToHost()) return null;
  if (deferMediaToHost(url, element)) return null;
  if (deferGoogleSlidesToHost(url)) return null;
  const title = (element.textContent ?? "").replace(/\s+/g, " ").trim();
  return { element, url, title };
}

/**
 * Whether data-docbox-ignore or the host's ignore options skip this click (shouldIgnoreDocboxPath,
 * public/docbox.js:35822).
 * Legacy tests every element on the event path, from the target up to <html>: the selector with
 * closest(), the text pattern against the element's text, aria-label or title, and alt, and the
 * URL pattern against its link URL. So a text pattern that matches any text on the page also
 * matches <html> and skips every link, as in legacy.
 */
export function ignoredPath(
  path: readonly EventTarget[],
  { selector, textRe, urlRe }: IgnoreOptions,
): boolean {
  for (const node of path) {
    if (!isElement(node)) continue;
    // data-docbox-ignore on the link or any ancestor (docbox.js:35832).
    const ignore = node.getAttribute("data-docbox-ignore");
    if (ignore !== null && ignore !== "false") return true;
    if (selector) {
      try {
        if (node.closest(selector)) return true;
      } catch {
        // An invalid selector skips nothing (docbox.js:35836).
      }
    }
    const text = textish(node);
    if (textRe && text) {
      textRe.lastIndex = 0;
      if (textRe.test(text)) return true;
    }
    const url = urlish(node);
    if (urlRe && url) {
      urlRe.lastIndex = 0;
      if (urlRe.test(url)) return true;
    }
  }
  return false;
}

/** docboxElementTextish, docbox.js:35804. */
function textish(el: Element): string {
  const text = (el.textContent ?? "").trim();
  const aria = el.getAttribute("aria-label") || el.getAttribute("title") || "";
  const alt = el.getAttribute("alt") || "";
  return `${text} ${aria} ${alt}`.replace(/\s+/g, " ").trim();
}

/** docboxElementUrlish, docbox.js:35812: the URL resolved against the page, or as written. */
function urlish(el: Element): string {
  const raw =
    el.getAttribute("href") ||
    el.getAttribute("data-href") ||
    el.getAttribute("data-url") ||
    el.getAttribute("data-docbox-url") ||
    el.getAttribute("data-pdf-url") ||
    "";
  if (!raw) return "";
  try {
    return new URL(raw, el.ownerDocument.baseURI).href;
  } catch {
    return raw;
  }
}

/** Temporary SharePoint credentials, including a URL wrapped by Google's viewer, stay on the host. */
export function hasTemporaryAuth(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  if (
    [...url.searchParams.keys()].some((key) => key.toLowerCase() === "tempauth")
  )
    return true;
  return url.hostname === "docs.google.com" &&
    /^\/(?:viewer|viewerng\/viewer)\/?$/.test(url.pathname)
    ? hasTemporaryAuth(url.searchParams.get("url") ?? "")
    : false;
}
